import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { ChevronDown, ChevronUp, Highlighter, Loader2, Minus, Plus, ScanText } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg, API_BASE, TOKEN_KEY } from "@/lib/api";
import { findMatches, findWordRuns } from "@/lib/textMatch";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

export const toolBtn = "flex h-7 w-7 items-center justify-center rounded-[7px] text-foreground/70 transition-colors hover:bg-black/[0.06] hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent";
export const pill = "ml-1 inline-flex items-center gap-1 rounded-full bg-black/[0.05] px-2 py-0.5 text-[11px] text-muted-foreground";
const SCAN_MIN = 25;
const GAP = 12;
const OCR_BUSY = ["pending", "running"];
const DOC_CACHE_MAX = 4;

/* Opened documents stay parsed in memory (a few at a time), so the second open of the same 114-page PDF is instant.
   Fresh opens read the file in byte ranges straight from the API – the first page shows before the rest has arrived. */
const docCache = new Map();
function openPdf(key, source) {
  const hit = docCache.get(key);
  if (hit) { hit.at = Date.now(); return hit.promise; }
  const promise = pdfjsLib.getDocument(source).promise;
  const entry = { promise, proxy: null, at: Date.now() };
  promise.then((d) => { entry.proxy = d; }, () => docCache.delete(key));
  docCache.set(key, entry);
  if (docCache.size > DOC_CACHE_MAX) {
    const [oldKey, old] = [...docCache.entries()].sort((a, b) => a[1].at - b[1].at)[0];
    docCache.delete(oldKey);
    old.proxy?.destroy();
  }
  return promise;
}
export const docDownloadUrl = (caseId, docId) => `${API_BASE}/projects/${caseId}/documents/${docId}/download?inline=1`;

/** Highlight boxes from the PDF's own text layer – fractions of the page box. `scanned` when the page has no usable text. */
async function textHits(pdfPage, queries) {
  const content = await pdfPage.getTextContent();
  const vp = pdfPage.getViewport({ scale: 1 });
  let str = "";
  const spans = [];
  for (const it of content.items) {
    if (!("str" in it)) continue;
    const start = str.length;
    str += it.str;
    spans.push({ it, start, end: str.length });
    str += it.hasEOL ? "\n" : " ";
  }
  if (str.replace(/\s+/g, "").length < SCAN_MIN) return { scanned: true, rects: [], groups: 0 };
  const rects = [];
  const matches = queries.length ? findMatches(str, queries) : [];
  matches.forEach((m, gi) => {
    for (const s of spans) {
      if (s.end <= m.start || s.start >= m.end || !s.it.str.trim()) continue;
      const [x, y] = [s.it.transform[4], s.it.transform[5]];
      const h = Math.hypot(s.it.transform[2], s.it.transform[3]) || s.it.height || 10;
      const w = s.it.width || 0;
      const [x1, y1, x2, y2] = vp.convertToViewportRectangle([x, y, x + w, y + h]);
      const n = Math.max(1, s.it.str.length);
      const fromFrac = Math.max(0, (m.start - s.start) / n);
      const toFrac = Math.min(1, (m.end - s.start) / n);
      const left = Math.min(x1, x2), width = Math.abs(x2 - x1);
      rects.push({ l: (left + width * fromFrac) / vp.width, t: Math.min(y1, y2) / vp.height, w: (width * (toFrac - fromFrac)) / vp.width, h: Math.abs(y2 - y1) / vp.height, g: gi });
    }
  });
  return { scanned: false, rects, groups: matches.length };
}

/** Highlight boxes from OCR words (already fractions of the page). */
export function ocrRects(words, queries) {
  const rects = [];
  findWordRuns(words || [], queries).forEach((run, gi) => {
    for (let i = run.first; i <= run.last; i++) {
      const w = words[i];
      rects.push({ l: w.x0, t: w.y0, w: w.x1 - w.x0, h: w.y1 - w.y0, g: gi });
    }
  });
  return rects;
}

export function Marks({ rects, page, show }) {
  if (!show || !rects?.length) return null;
  return rects.map((r, i) => <span key={i} className="pointer-events-none absolute rounded-[2px] bg-[rgba(255,208,0,0.42)] mix-blend-multiply ring-1 ring-[rgba(255,170,0,0.35)]" style={{ left: `${r.l * 100}%`, top: `${r.t * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%` }} data-testid={`pdf-highlight-${page}-${i}`} />);
}

const Page = React.memo(function Page({ pdf, no, size, scale, visible, hit, marking, ocrBusy, onSize }) {
  const canvasRef = useRef(null);
  const task = useRef(null);
  useEffect(() => {
    if (!visible || !pdf) return undefined;
    let alive = true;
    (async () => {
      const page = await pdf.getPage(no);
      if (!alive) return;
      const tr = performance.now();
      const nat = page.getViewport({ scale: 1 });
      if (Math.abs(nat.width - size.w) > 1 || Math.abs(nat.height - size.h) > 1) onSize(no, { w: nat.width, h: nat.height });
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vp = page.getViewport({ scale: scale * dpr });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = Math.floor(vp.width);
      canvas.height = Math.floor(vp.height);
      task.current?.cancel();
      task.current = page.render({ canvasContext: canvas.getContext("2d", { alpha: false }), viewport: vp });
      await task.current.promise;
      console.debug("[pdf] rendered page", no, Math.round(performance.now() - tr), "ms");
    })().catch(() => {});
    return () => { alive = false; task.current?.cancel(); };
  }, [pdf, no, scale, visible, size.w, size.h, onSize]);
  return (
    <div className="relative mx-auto bg-white shadow-[0_1px_2px_rgba(0,0,0,.08),0_8px_24px_-16px_rgba(0,0,0,.3)]" style={{ width: size.w * scale, height: size.h * scale }} data-testid={`pdf-page-${no}`} data-page={no} data-rendered={visible ? "1" : "0"} data-scanned={hit?.scanned ? "1" : undefined} data-marks={hit?.rects?.length || 0}>
      {visible ? <canvas ref={canvasRef} className="block h-full w-full" /> : null}
      {visible ? <Marks rects={hit?.rects} page={no} show={marking} /> : null}
      {visible && hit?.scanned && ocrBusy ? <span className="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2 py-0.5 text-[11px] text-muted-foreground shadow-sm"><Loader2 className="h-3 w-3 animate-spin" /> Leses med OCR</span> : null}
      <span className="absolute bottom-1.5 right-2 rounded-full bg-white/85 px-1.5 py-0.5 font-mono text-[10.5px] tabular-nums text-muted-foreground">{no}</span>
    </div>
  );
});

/** Reads the OCR words for scanned pages when they come into view – and keeps asking while OCR is still running. */
export function useOcrPages({ caseId, docId, enabled, initialStatus }) {
  const [ocr, setOcr] = useState({});
  const [status, setStatus] = useState(initialStatus || null);
  const cache = useRef(new Map());
  const want = useRef(new Set());
  const fetchPage = useCallback(async (no) => {
    if (!caseId || !docId) return null;
    try {
      const r = await api.get(`/projects/${caseId}/documents/${docId}/ocr`, { params: { page: no } });
      const d = r.data || {};
      if (d.words?.length || d.text) { cache.current.set(no, d); setOcr((o) => ({ ...o, [no]: d })); setStatus("done"); }
      else { setOcr((o) => ({ ...o, [no]: { words: [], empty: true } })); if (d.status) setStatus(d.status); }
      return d;
    } catch { return null; }
  }, [caseId, docId]);
  const request = useCallback((no) => { if (enabled && !cache.current.has(no) && !want.current.has(no)) { want.current.add(no); fetchPage(no); } }, [enabled, fetchPage]);
  useEffect(() => {
    if (!enabled || !OCR_BUSY.includes(status)) return undefined;
    const t = setInterval(() => { want.current.forEach((no) => { if (!cache.current.has(no)) fetchPage(no); }); }, 3000);
    return () => clearInterval(t);
  }, [enabled, status, fetchPage]);
  const run = useCallback(async () => {
    try { await api.post(`/projects/${caseId}/documents/${docId}/ocr`); setStatus("pending"); want.current.forEach((no) => cache.current.delete(no)); toast.success("Leser dokumentet med OCR – markeringen kommer når teksten er lest"); } catch (e) { toast.error(errMsg(e)); }
  }, [caseId, docId]);
  return { ocr, status, request, run };
}

/**
 * Our own PDF view (pdf.js): opens on the page a value came from, marks the passage in yellow – also on scanned pages,
 * where the words come from OCR – and lets you walk between hits. The server says which pages hold the text, so only
 * those (and the pages on screen) are read on the client; pages render lazily and stay cached between opens.
 */
export function PdfView({ url, page = 1, pages: hintPages, queries, caseId, docId, ocr: ocrMeta, className, compact = false }) {
  const [pdf, setPdf] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [fit, setFit] = useState(0);
  const [cur, setCur] = useState(page || 1);
  const [visible, setVisible] = useState(() => new Set());
  const [hits, setHits] = useState({});
  const [found, setFound] = useState(null); // server: pages holding the text + how many pages have text at all
  const [hitIdx, setHitIdx] = useState(-1);
  const [marking, setMarking] = useState(true);
  const [err, setErr] = useState(null);
  const scrollRef = useRef(null);
  const didJump = useRef(false);
  const scanned = useRef(new Set());
  const q = useMemo(() => (queries || []).filter((x) => x && String(x).trim().length >= 4).map(String), [queries]);
  const remote = Boolean(caseId && docId);
  const { ocr, status: ocrStatus, request: requestOcr, run: runOcr } = useOcrPages({ caseId, docId, enabled: remote, initialStatus: ocrMeta?.status });

  useEffect(() => {
    let alive = true;
    setPdf(null); setSizes([]); setErr(null); setHits({}); setHitIdx(-1); setFound(null); didJump.current = false; scanned.current = new Set();
    const key = remote ? `doc:${docId}` : `url:${url}`;
    const source = remote
      ? { url: docDownloadUrl(caseId, docId), httpHeaders: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` }, rangeChunkSize: 512 * 1024, disableStream: false, disableAutoFetch: true }
      : { url };
    const t0 = performance.now();
    openPdf(key, source).then(async (d) => {
      if (!alive) return;
      console.debug("[pdf] doc ready", Math.round(performance.now() - t0), "ms");
      const first = (await d.getPage(1)).getViewport({ scale: 1 });
      console.debug("[pdf] page1 size", Math.round(performance.now() - t0), "ms");
      if (!alive) return;
      // most documents have one page size; odd pages correct themselves when they render
      setSizes(Array.from({ length: d.numPages }, () => ({ w: first.width, h: first.height })));
      setPdf(d);
    }).catch((e) => alive && setErr(e?.message || "Kunne ikke åpne PDF-en"));
    return () => { alive = false; };
  }, [url, caseId, docId, remote]);
  const onSize = useCallback((no, s) => setSizes((all) => (all[no - 1] && (Math.abs(all[no - 1].w - s.w) > 1 || Math.abs(all[no - 1].h - s.h) > 1) ? all.map((x, i) => (i === no - 1 ? s : x)) : all)), []);

  // where the text sits – one server call over the stored page texts (own text layer and OCR alike)
  useEffect(() => {
    if (!remote || !q.length) { setFound(q.length ? { pages: [], failed: true } : { pages: [] }); return undefined; }
    let alive = true;
    api.get(`/projects/${caseId}/documents/${docId}/find`, { params: { q } })
      .then((r) => alive && setFound({ pages: (r.data.pages || []).map((p) => p.page), text_pages: r.data.text_pages, pages_total: r.data.pages_total }))
      .catch(() => alive && setFound({ pages: [], failed: true }));
    return () => { alive = false; };
  }, [remote, caseId, docId, q]);

  // read only the pages that matter: the referenced ones, the server's hits – and what is on screen when we had no answer
  const pre = useMemo(() => new Set([page || 1, ...(hintPages || [])]), [page, hintPages]);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!pdf || !found) return;
    const wanted = new Set([...pre, ...found.pages]);
    if (found.failed) visible.forEach((n) => wanted.add(n));
    const todo = [...wanted].filter((n) => n >= 1 && n <= pdf.numPages && !scanned.current.has(n));
    if (!todo.length) return;
    todo.forEach((n) => scanned.current.add(n));
    (async () => {
      const got = {};
      const t1 = performance.now();
      for (const no of todo) {
        try { got[no] = await textHits(await pdf.getPage(no), q); } catch { got[no] = { scanned: false, rects: [], groups: 0 }; }
      }
      console.debug("[pdf] text hits for", todo.join(","), Math.round(performance.now() - t1), "ms");
      if (mounted.current) setHits((h) => ({ ...h, ...got }));
    })();
  }, [pdf, found, pre, visible, q]);

  // scanned pages on screen get their words from OCR
  const ocrBusy = OCR_BUSY.includes(ocrStatus);
  useEffect(() => {
    Object.entries(hits).forEach(([no, h]) => { const n = Number(no); if (h.scanned && (visible.has(n) || pre.has(n) || found?.pages?.includes(n))) requestOcr(n); });
  }, [hits, visible, pre, found, requestOcr]);
  const merged = useMemo(() => {
    const out = { ...hits };
    Object.entries(ocr).forEach(([no, d]) => { const n = Number(no); if (out[n]?.scanned) { const rects = q.length ? ocrRects(d.words, q) : []; out[n] = { ...out[n], rects, groups: new Set(rects.map((r) => r.g)).size, ocr: Boolean(d.words?.length) || !ocrBusy }; } });
    return out;
  }, [hits, ocr, q, ocrBusy]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !sizes.length) return undefined;
    // fit to width, but never wider than a comfortable reading page – a 1920 px screen should not get a 1.5 m tall A4
    const ro = new ResizeObserver(() => { const w = Math.min(el.clientWidth - (compact ? 16 : 48), 1100); setFit(Math.max(0.3, w / sizes[0].w)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sizes, compact]);
  const scale = fit * zoom;
  const tops = useMemo(() => { let acc = 0; return sizes.map((s) => { const t = acc; acc += s.h * scale + GAP; return t; }); }, [sizes, scale]);
  const anchor = useRef(null);
  const setZoomKeep = useCallback((fn) => { const el = scrollRef.current; if (el && tops.length) { const i = Math.max(0, cur - 1); anchor.current = { i, frac: (el.scrollTop - tops[i]) / Math.max(1, sizes[i].h * scale) }; } setZoom(fn); }, [tops, cur, sizes, scale]);
  useEffect(() => { const el = scrollRef.current; const a = anchor.current; if (!el || !a || !tops.length) return; anchor.current = null; el.scrollTop = tops[a.i] + a.frac * sizes[a.i].h * scale; }, [tops, sizes, scale]);

  const refs = useRef([]);
  const ready = Boolean(pdf) && fit > 0 && sizes.length > 0;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !ready) return undefined;
    const io = new IntersectionObserver((entries) => {
      setVisible((prev) => { const next = new Set(prev); entries.forEach((e) => { const n = Number(e.target.dataset.page); if (e.isIntersecting) next.add(n); else next.delete(n); }); return next; });
    }, { root: el, rootMargin: "600px 0px" });
    refs.current.forEach((n) => n && io.observe(n));
    return () => io.disconnect();
  }, [ready]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !tops.length) return undefined;
    let raf = 0;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => { const mid = el.scrollTop + el.clientHeight / 3; let at = 1; for (let i = 0; i < tops.length; i++) { if (tops[i] <= mid) at = i + 1; else break; } setCur(at); }); };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { el.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [tops]);

  const goTo = useCallback((no, smooth = true) => {
    const el = scrollRef.current;
    if (!el || !tops.length) return;
    el.scrollTo({ top: Math.max(0, (tops[Math.min(no, tops.length) - 1] || 0) - 8), behavior: smooth ? "smooth" : "auto" });
  }, [tops]);
  useEffect(() => { if (ready && tops.length && !didJump.current) { didJump.current = true; requestAnimationFrame(() => goTo(page || 1, false)); } }, [ready, tops, page, goTo]);

  const hitPages = useMemo(() => Object.entries(merged).filter(([, r]) => r.rects?.length).map(([n]) => Number(n)).sort((a, b) => a - b), [merged]);
  const searching = !found || found.pages.some((n) => !merged[n] || (merged[n].scanned && !merged[n].ocr));
  const noTextPages = found?.pages_total && found.text_pages != null ? Math.max(0, found.pages_total - found.text_pages) : Object.values(merged).filter((r) => r.scanned).length;
  const ocrMissing = remote && noTextPages > 0 && !ocrBusy && ocrStatus !== "done";
  const step = (dir) => { if (!hitPages.length) return; const i = hitIdx < 0 ? (dir > 0 ? 0 : hitPages.length - 1) : (hitIdx + dir + hitPages.length) % hitPages.length; setHitIdx(i); goTo(hitPages[i]); };
  const hitLabel = hitPages.length ? `${hitIdx >= 0 ? `${hitIdx + 1} av ` : ""}${hitPages.length} ${hitPages.length === 1 ? "side" : "sider"} med treff` : searching || ocrBusy ? "Søker …" : "Fant ikke teksten i dokumentet";

  if (err) return <p className="p-6 text-[13px] text-[hsl(var(--v-blocked))]" data-testid="pdf-error">{err}</p>;
  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)} data-testid="pdf-view" data-page-current={cur} data-hits={hitPages.length} data-scan-done={!searching ? "1" : "0"} data-ocr-status={ocrStatus || undefined}>
      <div className={cn("flex shrink-0 flex-wrap items-center gap-1 border-b border-black/[0.06] bg-white/90 px-2 backdrop-blur", compact ? "min-h-9" : "min-h-10")} data-testid="pdf-toolbar">
        <button type="button" onClick={() => goTo(Math.max(1, cur - 1))} disabled={cur <= 1} className={toolBtn} aria-label="Forrige side" data-testid="pdf-prev"><ChevronUp className="h-4 w-4" /></button>
        <span className="font-mono text-[12px] tabular-nums text-foreground/80" data-testid="pdf-page-indicator">Side {cur}{sizes.length ? ` / ${sizes.length}` : ""}</span>
        <button type="button" onClick={() => goTo(Math.min(sizes.length, cur + 1))} disabled={!sizes.length || cur >= sizes.length} className={toolBtn} aria-label="Neste side" data-testid="pdf-next"><ChevronDown className="h-4 w-4" /></button>
        <span className="mx-1 h-4 w-px bg-black/[0.08]" />
        <button type="button" onClick={() => setZoomKeep((z) => Math.max(0.5, +(z - 0.15).toFixed(2)))} className={toolBtn} aria-label="Zoom ut" data-testid="pdf-zoom-out"><Minus className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => setZoomKeep(() => 1)} className="h-7 rounded-[7px] px-1.5 font-mono text-[11.5px] tabular-nums text-foreground/70 hover:bg-black/[0.06]" data-testid="pdf-zoom-reset">{Math.round(zoom * 100)} %</button>
        <button type="button" onClick={() => setZoomKeep((z) => Math.min(3, +(z + 0.15).toFixed(2)))} className={toolBtn} aria-label="Zoom inn" data-testid="pdf-zoom-in"><Plus className="h-3.5 w-3.5" /></button>
        <span className="flex-1" />
        {q.length ? (
          <span className="flex items-center gap-0.5" data-testid="pdf-hits">
            <button type="button" onClick={() => setMarking((v) => !v)} aria-pressed={marking} className={cn(toolBtn, marking && "bg-[rgba(255,208,0,0.35)] text-foreground")} aria-label="Marker kildetekst" title="Marker kildetekst" data-testid="pdf-mark-toggle"><Highlighter className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => step(-1)} disabled={hitPages.length < 2} className={toolBtn} aria-label="Forrige treff" data-testid="pdf-hit-prev"><ChevronUp className="h-3.5 w-3.5" /></button>
            <span className={cn("px-0.5 text-[12px]", hitPages.length ? "text-foreground/80" : "text-muted-foreground")} data-testid="pdf-hit-count">{hitLabel}</span>
            <button type="button" onClick={() => step(1)} disabled={hitPages.length < 2} className={toolBtn} aria-label="Neste treff" data-testid="pdf-hit-next"><ChevronDown className="h-3.5 w-3.5" /></button>
          </span>
        ) : null}
        {ocrBusy ? <span className={pill} data-testid="pdf-ocr-state"><Loader2 className="h-3 w-3 animate-spin" /> OCR {ocrMeta?.pages_done ?? 0}/{ocrMeta?.pages_total ?? "…"}</span>
          : ocrMissing ? <button type="button" onClick={runOcr} className={cn(pill, "hover:bg-black/[0.09] hover:text-foreground")} title={`${noTextPages} ${noTextPages === 1 ? "side" : "sider"} uten lesbar tekst – les dem med OCR`} data-testid="pdf-ocr-run"><ScanText className="h-3 w-3" /> Les med OCR</button>
            : ocrStatus === "done" && (noTextPages > 0 || ocrMeta?.pages_total) ? <span className={pill} title="Teksten på skannede sider er lest med OCR" data-testid="pdf-ocr-state"><ScanText className="h-3 w-3" /> OCR</span> : null}
      </div>
      {/* ph-no-capture / rr-block: session recorders must not snapshot a hundred canvases – that alone froze the viewer for seconds */}
      <div ref={scrollRef} className="ph-no-capture rr-block min-h-0 flex-1 overflow-auto bg-[hsl(210_20%_94%)] scrollbar-thin" data-testid="pdf-scroll">
        {!ready ? <div className="flex h-full items-center justify-center text-muted-foreground" data-testid="pdf-loading"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <div className={cn("flex flex-col", compact ? "p-2" : "p-6")} style={{ gap: GAP }}>
            {sizes.map((s, i) => <div key={i} ref={(n) => { refs.current[i] = n; }} data-page={i + 1}><Page pdf={pdf} no={i + 1} size={s} scale={scale} visible={visible.has(i + 1) || pre.has(i + 1)} hit={merged[i + 1]} marking={marking} ocrBusy={ocrBusy} onSize={onSize} /></div>)}
          </div>
        )}
      </div>
    </div>
  );
}
